/**
 * The browser's half of a multi-day rollover plan.
 *
 * smart-assign returns `rolloverParts` — Part A, B, C, … — planned by
 * `supabase/functions/_shared/rolloverSegmentPlanner.ts` from the canonical
 * dated authorities. OptiPlace and Catalyst persist those parts. Two things
 * can make the returned parts disagree with what is about to be written:
 *
 *   1. The start moves. Both creators bump a start that has slipped into the
 *      past to now+5. Part A was sized from the ORIGINAL start, so writing its
 *      minutes behind a later start pushes its OptiFinish past the day's close
 *      — exactly Floyd's Part A (10:11 → 22:20 on a 22:00 day).
 *   2. Rounding. Each part's OptiTime is converted back to book minutes; a
 *      per-part floor loses catalogued work across a long chain.
 *
 * `resolveRolloverPartsForStart` re-runs THE SAME planner from the actual
 * start over the workdays the server already resolved (`rolloverPlan.days`),
 * and `segmentBookMinutes` converts to book minutes while conserving the
 * ticket's total. The browser never invents a workday: if the server's days
 * cannot hold the re-planned work, the caller is told to refresh.
 */

import {
  planRolloverSegments,
  segmentBookMinutes,
  type PlannerDay,
} from '../../supabase/functions/_shared/rolloverSegmentPlanner';
import { getMinutesInShopTZ, makeUTCFromShopMinutes } from '@/utils/timezoneMath';
import { getShopDayLabel } from '@/utils/shopTime';

export { segmentBookMinutes };

export interface RankingRolloverPart {
  partLabel: string;
  dayOffset: number;
  startTime: string;
  finishTime: string;
  /** Technician OptiTime for this part. */
  minutes: number;
  sequence?: number;
  scheduledDate?: string;
  remainingAfterMinutes?: number;
}

export interface RankingRolloverPlan {
  totalWorkMinutes: number;
  startDateStr: string;
  startMin: number;
  days: PlannerDay[];
}

export type ResolvedRolloverParts =
  | { ok: true; parts: RankingRolloverPart[]; replanned: boolean }
  | { ok: false; reason: 'NO_PLAN_FOR_NEW_START' };

function sameMinute(a: string, b: string): boolean {
  return Math.floor(new Date(a).getTime() / 60_000) === Math.floor(new Date(b).getTime() / 60_000);
}

function label(sequence: number): string {
  let n = sequence;
  let s = '';
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return `Part ${s}`;
}

/**
 * The parts to persist when Part A will actually start at `startIso`.
 *
 * Unchanged start → the server's parts, verbatim. A later start → the same
 * work re-planned from it over the server-resolved workdays.
 */
export function resolveRolloverPartsForStart(input: {
  parts: RankingRolloverPart[];
  plan?: RankingRolloverPlan | null;
  startIso: string;
  shopTimezone: string;
}): ResolvedRolloverParts {
  const { parts, plan, startIso, shopTimezone } = input;
  if (parts.length === 0 || sameMinute(parts[0].startTime, startIso)) {
    return { ok: true, parts, replanned: false };
  }
  if (!plan || !Array.isArray(plan.days) || plan.days.length === 0) {
    return { ok: false, reason: 'NO_PLAN_FOR_NEW_START' };
  }
  const byDate = new Map(plan.days.map((d) => [d.dateStr, d]));
  const startDateStr = getShopDayLabel(startIso, shopTimezone);
  const replanned = planRolloverSegments({
    totalWorkMinutes: plan.totalWorkMinutes,
    startDateStr,
    startMin: getMinutesInShopTZ(startIso, shopTimezone),
    resolveDay: (d) => byDate.get(d) ?? null,
  });
  if (!replanned.complete || replanned.segments.length === 0) {
    return { ok: false, reason: 'NO_PLAN_FOR_NEW_START' };
  }
  const first = replanned.segments[0].dateStr;
  const dayMs = (d: string) => Date.parse(`${d}T00:00:00Z`);
  return {
    ok: true,
    replanned: true,
    parts: replanned.segments.map((s) => ({
      partLabel: label(s.sequence),
      sequence: s.sequence,
      dayOffset: Math.round((dayMs(s.dateStr) - dayMs(first)) / 86_400_000),
      scheduledDate: s.dateStr,
      startTime: makeUTCFromShopMinutes(s.dateStr, s.startMin, shopTimezone).toISOString(),
      finishTime: makeUTCFromShopMinutes(s.dateStr, s.finishMin, shopTimezone).toISOString(),
      minutes: s.workMinutes,
      remainingAfterMinutes: s.remainingAfterMinutes,
    })),
  };
}

/** One continuation row's schedule and lineage — the fields every creator shares. */
export interface RolloverContinuationLink {
  id: string;
  /** Part B = 2, Part C = 3, … */
  sequence_index: number;
  /** Every part points at the ROOT (fetchRolloverChain reads the chain by it). */
  parent_ticket_id: string;
  /** Each part follows the one before it (8G.2D.6A: a chain, not a fan). */
  rollover_of_ticket_id: string;
  start_time: string;
  scheduled_date: string;
  /** Book minutes for this part. */
  predicted_minutes: number;
  split_reason: 'EOD_ROLLOVER';
}

export interface RolloverChainShape {
  root: {
    start_time: string;
    scheduled_date: string;
    predicted_minutes: number;
    sequence_index: 1;
    /** OptiTime still to do after Part A. */
    remaining_minutes_at_split: number;
    split_reason: 'EOD_ROLLOVER';
  };
  continuations: RolloverContinuationLink[];
}

/**
 * The persisted shape of a multi-day plan: Part A's schedule fields and one
 * lineage-linked continuation per further part. OptiPlace and Catalyst both
 * build their rows from THIS, so the two creators cannot persist the same plan
 * differently — and a proof that calls it persists exactly what they do.
 */
export function buildRolloverChain(input: {
  rootTicketId: string;
  parts: RankingRolloverPart[];
  totalBookMinutes: number;
  efficiencyPct: number;
  bufferPct: number;
  shopTimezone: string;
  nowMs: number;
}): RolloverChainShape {
  const { rootTicketId, parts, shopTimezone } = input;
  const book = segmentBookMinutes(parts.map((p) => p.minutes), input.totalBookMinutes, input.efficiencyPct, input.bufferPct);
  const dateOf = (p: RankingRolloverPart) => p.scheduledDate ?? getShopDayLabel(p.startTime, shopTimezone);
  const continuations: RolloverContinuationLink[] = [];
  let previous = rootTicketId;
  for (let i = 1; i < parts.length; i++) {
    const id = `${rootTicketId}-ROLL${i}-${input.nowMs}`;
    continuations.push({
      id,
      sequence_index: i + 1,
      parent_ticket_id: rootTicketId,
      rollover_of_ticket_id: previous,
      start_time: new Date(parts[i].startTime).toISOString(),
      scheduled_date: dateOf(parts[i]),
      predicted_minutes: book[i],
      split_reason: 'EOD_ROLLOVER',
    });
    previous = id;
  }
  return {
    root: {
      start_time: new Date(parts[0].startTime).toISOString(),
      scheduled_date: dateOf(parts[0]),
      predicted_minutes: book[0],
      sequence_index: 1,
      remaining_minutes_at_split: parts.slice(1).reduce((sum, p) => sum + p.minutes, 0),
      split_reason: 'EOD_ROLLOVER',
    },
    continuations,
  };
}

/**
 * B-W5 (LEDGER D-3) — what OptiPlace's rollover preview showed, as the CLIENT attests it, for the booking-approval
 * record. Pure. The server stores this as `original_proposal: { basis: 'client_attested', verified_by_server: false }`
 * — it is the client's statement, never proof that a preview was displayed. Ids / dates / times / minutes only.
 *   shownParts      the parts the ranking returned (what the Day 1 / Day 2 / part list rendered)
 *   committedParts  the parts actually built (re-planned when the start moved — then `replanned_after_preview`)
 *   badgeShown      whether the "Rollover Required" badge was rendered for the chosen technician
 * Due-out impact is not on the preview today, so it is attested as not shown (honest, not inferred).
 */
export function buildOptiPlaceRolloverAttestation(input: {
  shownParts: readonly RankingRolloverPart[];
  committedParts: readonly RankingRolloverPart[];
  badgeShown: boolean;
}): Record<string, unknown> {
  const part = (p: RankingRolloverPart) => ({
    label: p.partLabel, scheduled_date: p.scheduledDate ?? null, day_offset: p.dayOffset,
    start_time: p.startTime, finish_time: p.finishTime, minutes: p.minutes,
  });
  const shown = input.shownParts.map(part);
  const committed = input.committedParts.map(part);
  return {
    surface: 'optiplace_rollover_preview',
    badge_shown: input.badgeShown ? 'rollover_required' : null,
    parts_shown: shown,
    replanned_after_preview: JSON.stringify(shown) !== JSON.stringify(committed),
    due_out_impact_shown: false,
  };
}
